import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Users, CheckCircle2, Clock, AlertTriangle, Award } from 'lucide-react';

import { getMyTeamTraining } from '../../services/trainingReportService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PersonCell from '../../components/ui/PersonCell';
import DataTable from '../../components/ui/DataTable';

const Container = styled.div`padding: 16px 24px 24px;`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
`;

const KpiCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KpiIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg || '#E0F2FE'};
  color: ${({ $color }) => $color || '#0291EF'};
`;

const KpiLabel = styled.div`font-size: 12px; color: #64748b;`;
const KpiValue = styled.div`font-size: 22px; font-weight: 800; color: #004242;`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const RateBar = styled.div`
  position: relative;
  width: 80px;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
`;

const RateFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $pct }) => $pct >= 80 ? '#16A34A' : $pct >= 50 ? '#F59E0B' : '#EF4444'};
`;

const OverdueBadge = styled.span`
  display: inline-block;
  background: ${({ $n }) => $n > 0 ? '#FEE2E2' : '#F0FDF4'};
  color: ${({ $n }) => $n > 0 ? '#991B1B' : '#166534'};
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
`;

export default function MyTeamTrainingPage() {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyTeamTraining()
      .then((res) => setData(res.data?.data))
      .finally(() => setLoading(false));
  }, []);

  const members = data?.members || [];

  const columns = useMemo(() => [
    { name: 'userId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'fullName',
      label: t('myTeamTraining.member'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        customBodyRender: (_, tableMeta) => {
          const m = members[tableMeta.rowIndex];
          return <PersonCell name={m.fullName} profilePicture={m.profilePicture} />;
        },
      },
    },
    { name: 'assigned', label: t('myTeamTraining.assigned'), options: { filter: false, sort: true } },
    {
      name: 'completed',
      label: t('myTeamTraining.completed'),
      options: { filter: false, sort: true, customBodyRender: (v) => <span style={{ color: '#16A34A', fontWeight: 700 }}>{v}</span> },
    },
    { name: 'inProgress', label: t('myTeamTraining.inProgress'), options: { filter: false, sort: true } },
    {
      name: 'overdue',
      label: t('myTeamTraining.overdue'),
      options: { filter: false, sort: true, customBodyRender: (v) => <OverdueBadge $n={v}>{v}</OverdueBadge> },
    },
    {
      name: 'completionRate',
      label: t('myTeamTraining.completionRate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RateBar><RateFill $pct={v} /></RateBar>
            <span style={{ fontSize: 12, color: '#64748b', minWidth: 36 }}>%{Number(v).toFixed(0)}</span>
          </div>
        ),
      },
    },
  ], [t, members]);

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!data) return <Container>{t('common.noData')}</Container>;

  if (data.teamSize === 0) {
    return (
      <Container>
        <Breadcrumbs items={[{ label: t('menu.myTeamTraining') }]} />
        <Card>{t('myTeamTraining.noTeam')}</Card>
      </Container>
    );
  }

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('menu.myTeamTraining') }]} />

      <KpiGrid>
        <KpiCard>
          <KpiIconBox $bg="#E0F2FE" $color="#0291EF"><Users size={20} /></KpiIconBox>
          <div>
            <KpiLabel>{t('myTeamTraining.teamSize')}</KpiLabel>
            <KpiValue>{data.teamSize}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#F0FDF4" $color="#16A34A"><CheckCircle2 size={20} /></KpiIconBox>
          <div>
            <KpiLabel>{t('myTeamTraining.completed')}</KpiLabel>
            <KpiValue>{data.completedCount}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#FEF3C7" $color="#F59E0B"><Clock size={20} /></KpiIconBox>
          <div>
            <KpiLabel>{t('myTeamTraining.inProgress')}</KpiLabel>
            <KpiValue>{data.inProgressCount}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#FEE2E2" $color="#DC2626"><AlertTriangle size={20} /></KpiIconBox>
          <div>
            <KpiLabel>{t('myTeamTraining.overdue')}</KpiLabel>
            <KpiValue>{data.overdueCount}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#ECFDF5" $color="#10B981"><Award size={20} /></KpiIconBox>
          <div>
            <KpiLabel>{t('myTeamTraining.completionRate')}</KpiLabel>
            <KpiValue>{data.completionRate != null ? `%${data.completionRate}` : '—'}</KpiValue>
          </div>
        </KpiCard>
      </KpiGrid>

      <DataTable
        title={t('myTeamTraining.members')}
        serverSide={false}
        columns={columns}
        data={members}
        pageSize={10}
      />
    </Container>
  );
}
